import type { ClassDef, ClassId, HeroClassId } from "../sim/types";

function cls(partial: ClassDef): ClassDef {
  return partial;
}

export const CLASSES: Record<ClassId, ClassDef> = {
  warrior: cls({
    id: "warrior",
    name: "Warrior",
    role: "Bruiser",
    law: "Combat activates the Warrior. Facing and restraint protect them; aggression makes them faster and more dangerous.",
    color: "#e0a020",
    maxHp: 10,
    speed: 5.2,
    basicCd: 0.85,
    skill: "bulls_charge",
    ult: "dismember",
    autos: ["cleave"],
    passives: ["shields_up", "adrenaline"],
    playable: true,
  }),
  ranger: cls({
    id: "ranger",
    name: "Ranger",
    role: "Marksman",
    law: "Stillness makes both the Ranger and their targets more vulnerable to decisive shots.",
    color: "#5cb85c",
    maxHp: 10,
    speed: 5.4,
    basicCd: 0.95,
    skill: "tumble",
    ult: "heavy_draw",
    autos: ["crippling_shot"],
    passives: ["marksmanship", "apply_poison"],
    playable: true,
  }),
  priest: cls({
    id: "priest",
    name: "Priest",
    role: "Support",
    law: "Priest magic heals the innocent and punishes the guilty. Violence creates Ire.",
    color: "#f0e2a8",
    maxHp: 10,
    speed: 5.1,
    basicCd: 0.8,
    skill: "be_not_afraid",
    ult: "cleanse_evil",
    autos: ["mending_touch"],
    passives: ["penance", "shield_of_faith"],
    playable: true,
  }),
  cultist: cls({
    id: "cultist",
    name: "Cultist",
    role: "Drain",
    law: "Injury is food. Health can be moved, refined, and harvested; death changes the Cultist's operating mode instead of ending it.",
    color: "#a83c5a",
    maxHp: 10,
    speed: 5.2,
    basicCd: 0.8,
    skill: "life_transfer",
    ult: "flesh_of_my_flesh",
    autos: ["reap"],
    passives: ["defy_death", "insatiable"],
    playable: true,
  }),
  psychic: cls({
    id: "psychic",
    name: "Psychic",
    role: "Disruptor",
    law: "The Psychic involuntarily shares nearby suffering, has limited resistance to it, and can bestow the same vulnerability on others.",
    color: "#9b7ed9",
    maxHp: 10,
    speed: 5.1,
    basicCd: 0.9,
    skill: "feel_my_pain",
    ult: "blackout",
    autos: ["phantom_pain"],
    passives: ["empathy", "mental_toughness"],
    playable: true,
  }),
  assassin: cls({
    id: "assassin",
    name: "Assassin",
    role: "Skirmisher",
    law: "Attention is a resource. The Assassin harasses from just outside melee, disappears when ignored, and chooses between ranged pressure and defensive close combat.",
    color: "#7a8898",
    maxHp: 10,
    speed: 5.6,
    basicCd: 0.75,
    skill: "close_quarters",
    ult: "smoke_bomb",
    autos: ["serpent_fan", "clean_cuts"],
    passives: ["beneath_notice", "ambush", "kunai_fleet", "sword_block"],
    playable: true,
  }),
  dervish: cls({
    id: "dervish",
    name: "Dervish",
    role: "Enchantment",
    law: "Magic becomes communal around the Dervish. They grow stronger as enchantments proliferate, then remove the wrong magic from the wrong side.",
    color: "#d4c06a",
    maxHp: 10,
    speed: 5.3,
    basicCd: 0.82,
    skill: "right_from_wrong",
    ult: "avatar",
    autos: ["scythe"],
    passives: ["shared_fate", "mysticism"],
    playable: true,
  }),
  ritualist: cls({
    id: "ritualist",
    name: "Ritualist",
    role: "Spirits",
    law: "Spirits are slow-moving batteries. Their Health is stored spiritual work, and the Ritualist projects power through other bodies rather than directly.",
    color: "#6bb8c8",
    maxHp: 10,
    speed: 5.0,
    basicCd: 0.95,
    skill: "invocation",
    ult: "soul_cleave",
    autos: ["lesser_invocation"],
    passives: ["spirit_world", "spirit_arms"],
    playable: true,
  }),
  paragon: cls({
    id: "paragon",
    name: "Paragon",
    role: "Captain",
    law: "The Paragon declares their side righteous. Enemies are punished for helping their team or hurting yours, and allied action shares a cadence.",
    color: "#e8b84a",
    maxHp: 10,
    speed: 5.2,
    basicCd: 0.88,
    skill: "rally",
    ult: "turn_the_tide",
    autos: ["spear"],
    passives: ["retribution", "vindication"],
    playable: true,
  }),
  paladin: cls({
    id: "paladin",
    name: "Paladin",
    role: "Anchor",
    law: "The Paladin claims ground. They are slow, extremely difficult to kill, supportive to nearby allies, and dangerous because staying near them eventually means getting stunned.",
    color: "#c9b37a",
    maxHp: 10,
    speed: 4.4,
    basicCd: 1.05,
    skill: "sanctuary",
    ult: "hallowed_ground",
    autos: ["hammerfall"],
    passives: ["sacred_bulwark", "gathering_force"],
    playable: true,
  }),
  skeleton: cls({
    id: "skeleton",
    name: "Skeleton",
    role: "Minion",
    law: "A harvested corpse, given work.",
    color: "#c8c2b4",
    maxHp: 4,
    speed: 4.4,
    basicCd: 1.1,
    skill: "",
    ult: "",
    autos: ["minion_slash"],
    passives: [],
    playable: false,
  }),
  golem: cls({
    id: "golem",
    name: "Flesh Golem",
    role: "Minion",
    law: "All the Cultist's remaining health, given a body.",
    color: "#8a3048",
    maxHp: 8,
    speed: 3.8,
    basicCd: 1.2,
    skill: "",
    ult: "",
    autos: ["minion_slam"],
    passives: [],
    playable: false,
  }),
  spirit: cls({
    id: "spirit",
    name: "Spirit",
    role: "Battery",
    law: "Stored spiritual work. Drifts toward the living.",
    color: "#9fd8e4",
    maxHp: 4,
    speed: 1.6,
    basicCd: 99,
    skill: "",
    ult: "",
    autos: [],
    passives: ["spirit_equilibrium"],
    playable: false,
  }),
};

export const HERO_ORDER: HeroClassId[] = [
  "warrior",
  "ranger",
  "priest",
  "cultist",
  "psychic",
  "assassin",
  "dervish",
  "ritualist",
  "paragon",
  "paladin",
];

export function classById(id: ClassId): ClassDef {
  return CLASSES[id];
}
